import type { SingleProxy } from "@/api/custom_types";
import type { ColumnSpec } from "@/constants/types/csv";
import { allowEmpty, applyRowIsValid } from "@/utils/csv-helpers";
import { normalizeIpAddress } from "@/utils/normalize";
import type { TranslationF } from "@/utils/type-utils";
import type { RowData } from "./Index";

export const createEmptyRow = (): RowData => {
	return {
		ipAddress: {
			value: null,
			disabled: false,
			isValid: true,
		},
		port: {
			value: null,
			disabled: false,
			isValid: true,
		},
		username: {
			value: null,
			disabled: false,
			isValid: true,
		},
		password: {
			value: null,
			disabled: false,
			isValid: true,
		},
		isRowValid: true,
	};
};

export const makeColumnSpecs = (t: TranslationF): ColumnSpec[] => [
	{
		key: "ipAddress.value",
		header: t("proxy.new.csvIpAddress"),
		type: "text",
		validator: allowEmpty((v) => {
			try {
				const normalized = normalizeIpAddress(v.toString());
				new URL(normalized);
				return true;
			} catch {
				return t("validation.invalidUrl");
			}
		}),
	},
	{
		key: "port.value",
		header: t("proxy.new.csvPort"),
		type: "text",
		validator: allowEmpty((v) => {
			const num = Number(v);
			if (!Number.isInteger(num) || num < 1 || num > 65535) {
				return t("validation.IntegerBetween", {
					field: t("proxy.new.csvPort"),
					min: 1,
					max: 65535,
				});
			}
			return true;
		}),
	},
	{
		key: "username.value",
		header: t("proxy.new.csvUsername"),
		type: "text",
		validator: allowEmpty((_v) => true),
	},
	{
		key: "password.value",
		header: t("proxy.new.csvPassword"),
		type: "text",
		validator: allowEmpty((v) => {
			if (typeof v !== "string") {
				return t("validation.string", {
					field: t("proxy.new.csvPassword"),
				});
			}
			if (v.length < 6) {
				return t("validation.atLeast", {
					field: t("proxy.new.csvPassword"),
					num: 6,
				});
			}
			if (!/[a-z]/.test(v) || !/[A-Z]/.test(v)) {
				return t("validation.containUppercaseAndLowercase", {
					field: t("proxy.new.csvPassword"),
				});
			}
			return true;
		}),
	},
];

export function isRowEmpty(row: RowData): boolean {
	return (
		(row.username.value == null || row.username.value === "") &&
		(row.password.value == null || row.password.value === "") &&
		(row.ipAddress.value == null || row.ipAddress.value === "") &&
		(row.port.value == null || row.port.value === "")
	);
}

export function isRowValid(row: RowData): boolean {
	if (isRowEmpty(row)) return true;

	const isMainFieldsValid =
		row.ipAddress.value &&
		row.ipAddress.value !== "" &&
		row.port.value &&
		row.port.value !== "";

	return isMainFieldsValid;
}

const validateRowData = (
	row: RowData,
	index: number,
	t: TranslationF,
): string | null => {
	if (!isRowValid(row)) {
		return t("validation.missingRowValues", { index: index + 1 });
	}

	return row.isRowValid ? "" : t("validation.rowNotValid", { row: index + 1 });
};

export const validateData = (
	data: RowData[],
	t: TranslationF,
): { isValid: boolean; errorMessage: string } => {
	const hasAnyData = data.some((row) => !isRowEmpty(row));
	if (!hasAnyData) {
		return { isValid: false, errorMessage: "" };
	}

	for (let index = 0; index < data.length; index++) {
		const row = data[index];
		applyRowIsValid(row);
		const rowError = validateRowData(row, index, t);
		if (rowError) {
			return { isValid: false, errorMessage: rowError };
		}
	}

	return { isValid: true, errorMessage: "" };
};

export function createProxyBatchItems(rows: RowData[]): SingleProxy[] {
	return rows
		.filter((row) => !isRowEmpty(row))
		.map((row) => {
			return {
				ip_address: normalizeIpAddress(row.ipAddress.value.toString()),
				port: Number(row.port.value),
				username: row.username.value
					? row.username.value.toString()
					: undefined,
				password: row.password.value
					? row.password.value.toString()
					: undefined,
			};
		});
}
